// biome-ignore-all lint/a11y/useAriaPropsSupportedByRole: shared component anatomy.
// biome-ignore-all lint/a11y/useFocusableInteractive: shared component anatomy.
// biome-ignore-all lint/a11y/noAutofocus: shared component anatomy.
// biome-ignore-all lint/style/useTemplate: shared component anatomy.
// biome-ignore-all lint/complexity/useIndexOf: shared component anatomy.
// biome-ignore-all lint/security/noDangerouslySetInnerHtml: shared component anatomy.
// biome-ignore-all lint/suspicious/noArrayIndexKey: shared component anatomy.
// biome-ignore-all lint/a11y/useSemanticElements: shared component anatomy.
// biome-ignore-all lint/a11y/noInteractiveElementToNoninteractiveRole: shared component anatomy.
// biome-ignore-all lint/a11y/noStaticElementInteractions: shared component anatomy.
// biome-ignore-all lint/a11y/useKeyWithClickEvents: shared component anatomy.
// biome-ignore-all lint/a11y/noSvgWithoutTitle: shared component anatomy.
// biome-ignore-all lint/correctness/useExhaustiveDependencies: shared component anatomy.
import type { ProviderId } from "@thilke/ui/lib/chains";
import { cn } from "@thilke/ui/lib/utils";

type IconProps = React.SVGProps<SVGSVGElement>;

// Brand colors per provider
const brandColors: Record<ProviderId, string> = {
  ethereum: "#627EEA",
  bitcoin: "#F7931A",
  solana: "#9945FF",
  polygon: "#7B3FE4",
  base: "#0052FF",
  arbitrum: "#28A0F0",
  optimism: "#FF0420",
  avalanche: "#E84142",
  bnb: "#F3BA2F",
  litecoin: "#345D9D",
  paypal: "#003087",
  kofi: "#FF5E5B",
  patreon: "#FF424D",
};

function EthereumIcon({ color, ...props }: IconProps & { color?: string }) {
  const c = color ?? "currentColor";
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" {...props}>
      <g fillRule="nonzero">
        <path opacity=".6" fill={c} d="M16.498 4v8.87l7.497 3.35z" />
        <path fill={c} d="M16.498 4L9 16.22l7.498-3.35z" />
        <path opacity=".6" fill={c} d="M16.498 21.968v6.027L24 17.616z" />
        <path fill={c} d="M16.498 27.995v-6.028L9 17.616z" />
        <path
          opacity=".2"
          fill={c}
          d="M16.498 20.573l7.497-4.353-7.497-3.348z"
        />
        <path opacity=".6" fill={c} d="M9 16.22l7.498 4.353v-7.701z" />
      </g>
    </svg>
  );
}

function BitcoinIcon({ color, ...props }: IconProps & { color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      fill={color ?? "currentColor"}
      {...props}
    >
      <path
        fillRule="nonzero"
        d="M23.189 14.02c.314-2.096-1.283-3.223-3.465-3.975l.708-2.84-1.728-.43-.69 2.765c-.454-.114-.92-.22-1.385-.326l.695-2.783L15.596 6l-.708 2.839c-.376-.086-.746-.17-1.104-.26l.002-.009-2.384-.595-.46 1.846s1.283.294 1.256.312c.7.175.826.638.805 1.006l-.806 3.235c.048.012.11.03.18.057l-.183-.045-1.13 4.532c-.086.212-.303.531-.793.41.018.025-1.256-.313-1.256-.313l-.858 1.978 2.25.561c.418.105.828.215 1.231.318l-.715 2.872 1.727.43.708-2.84c.472.127.93.245 1.378.357l-.706 2.828 1.728.43.715-2.866c2.948.558 5.164.333 6.097-2.333.752-2.146-.037-3.385-1.588-4.192 1.13-.26 1.98-1.003 2.207-2.538zm-3.95 5.538c-.533 2.147-4.148.986-5.32.695l.95-3.805c1.172.293 4.929.872 4.37 3.11zm.535-5.569c-.487 1.953-3.495.96-4.47.717l.86-3.45c.975.243 4.118.696 3.61 2.733z"
      />
    </svg>
  );
}

function SolanaIcon({ color, ...props }: IconProps & { color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      fill={color ?? "currentColor"}
      {...props}
    >
      <path d="M9.925 19.687a.59.59 0 01.415-.17h14.366a.29.29 0 01.207.497l-2.838 2.815a.59.59 0 01-.415.171H7.294a.291.291 0 01-.207-.498l2.838-2.815zm0-10.517A.59.59 0 0110.34 9h14.366c.261 0 .392.314.207.498l-2.838 2.815a.59.59 0 01-.415.17H7.294a.291.291 0 01-.207-.497L9.925 9.17zm12.15 5.225a.59.59 0 00-.415-.17H7.294a.291.291 0 00-.207.498l2.838 2.815c.11.109.26.17.415.17h14.366a.291.291 0 00.207-.498l-2.838-2.815z" />
    </svg>
  );
}

function PolygonIcon({ color, ...props }: IconProps & { color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      fill={color ?? "currentColor"}
      {...props}
    >
      <path d="M21.092 12.693c-.369-.215-.848-.215-1.254 0l-2.879 1.654-1.955 1.078-2.879 1.653c-.369.216-.848.216-1.254 0l-2.288-1.294c-.369-.215-.627-.61-.627-1.042V12.19c0-.431.221-.826.627-1.042l2.25-1.258c.37-.216.85-.216 1.256 0l2.25 1.258c.37.216.628.611.628 1.042v1.654l1.955-1.115v-1.653a1.16 1.16 0 00-.627-1.042l-4.17-2.372c-.369-.216-.848-.216-1.254 0l-4.244 2.372A1.16 1.16 0 006 11.076v4.78c0 .432.221.827.627 1.043l4.244 2.372c.369.215.849.215 1.254 0l2.879-1.618 1.955-1.114 2.879-1.617c.369-.216.848-.216 1.254 0l2.251 1.258c.37.215.627.61.627 1.042v2.552c0 .431-.22.826-.627 1.042l-2.25 1.294c-.37.216-.85.216-1.255 0l-2.251-1.258c-.37-.216-.628-.611-.628-1.042v-1.654l-1.955 1.115v1.653c0 .431.221.827.627 1.042l4.244 2.372c.369.216.848.216 1.254 0l4.244-2.372c.369-.215.627-.61.627-1.042v-4.78a1.16 1.16 0 00-.627-1.042l-4.28-2.409z" />
    </svg>
  );
}

function AvalancheIcon({ color, ...props }: IconProps & { color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      fill={color ?? "currentColor"}
      {...props}
    >
      <path d="M11.518 22.75H8.49c-.636 0-.95 0-1.142-.123A.77.77 0 017 22.025c-.012-.226.145-.503.46-1.055l7.472-13.193c.318-.56.48-.84.682-.944a.77.77 0 01.698 0c.203.104.364.384.682.944l1.536 2.686.008.014c.343.6.517.906.593 1.226a2.26 2.26 0 010 1.066c-.076.323-.249.63-.597 1.24l-3.926 6.95-.01.017c-.346.606-.52.913-.764 1.145a2.284 2.284 0 01-.93.54c-.319.089-.675.089-1.387.089zm7.643 0h4.336c.64 0 .962 0 1.154-.126a.768.768 0 00.348-.607c.011-.219-.142-.484-.443-1.005l-.032-.054-2.172-3.722-.025-.042c-.305-.517-.46-.778-.657-.879a.762.762 0 00-.693 0c-.2.104-.36.377-.678.925l-2.165 3.722-.007.013c-.317.548-.476.821-.464 1.046a.777.777 0 00.348.606c.188.123.51.123 1.15.123z" />
    </svg>
  );
}

function BnbIcon({ color, ...props }: IconProps & { color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      fill={color ?? "currentColor"}
      {...props}
    >
      <path d="M12.116 14.404L16 10.52l3.886 3.886 2.26-2.26L16 6l-6.144 6.144 2.26 2.26zM6 16l2.26-2.26L10.52 16l-2.26 2.26L6 16zm6.116 1.596L16 21.48l3.886-3.886 2.26 2.259L16 26l-6.144-6.144-.003-.003 2.263-2.257zM21.48 16l2.26-2.26L26 16l-2.26 2.26L21.48 16zm-3.188-.002h.002V16L16 18.294l-2.291-2.29-.004-.004.004-.003.401-.402.195-.195L16 13.706l2.293 2.293z" />
    </svg>
  );
}

function LitecoinIcon({ color, ...props }: IconProps & { color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      fill={color ?? "currentColor"}
      {...props}
    >
      <path d="M10.427 19.214L9 19.768l.688-2.759 1.444-.58L13.213 8h5.129l-1.519 6.196 1.41-.571-.68 2.75-1.427.571-.848 3.483H23L22.127 24H9.252z" />
    </svg>
  );
}

function BaseIcon({ color, ...props }: IconProps & { color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      fill={color ?? "currentColor"}
      {...props}
    >
      <path d="M16.002 26c5.523 0 10-4.477 10-10s-4.477-10-10-10C10.822 6 6.57 9.947 6.04 14.97h13.188v2.06H6.04C6.57 22.053 10.822 26 16.002 26z" />
    </svg>
  );
}

function ArbitrumIcon({ color, ...props }: IconProps & { color?: string }) {
  const c = color ?? "currentColor";
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" {...props}>
      <path
        fill={c}
        d="M16.62 7.005l-8.4 13.37 2.078 3.563L19.1 9.577l-1.24-1.916a1.52 1.52 0 00-1.24-.656z"
      />
      <path
        fill={c}
        d="M22.1 9.577l-4.35 6.917 3.112 4.958L25.22 13.5l-1.88-3.267a1.52 1.52 0 00-1.24-.656z"
      />
      <path
        fill={c}
        opacity=".6"
        d="M6.54 21.96l1.76 3.04 1.84-1.06-1.76-3.04-1.84 1.06zm16.28-1.508l1.84 1.06 1.76-3.04-1.84-1.06-1.76 3.04z"
      />
    </svg>
  );
}

function OptimismIcon({ color, ...props }: IconProps & { color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill={color ?? "currentColor"}
      {...props}
    >
      <path d="M9.61 8.705c.462 0 .878.078 1.244.234.366.15.653.378.863.684.21.3.317.66.317 1.08 0 .126-.015.286-.045.479a20.4 20.4 0 01-.324 1.558c-.21.823-.574 1.438-1.09 1.846-.517.402-1.208.604-2.073.604-.714 0-1.3-.168-1.755-.504-.451-.342-.676-.834-.676-1.459 0-.132.015-.27.045-.486.078-.432.19-.952.334-1.559.408-1.651 1.461-2.477 3.16-2.477zm4.17.09h2.396c.667 0 1.174.138 1.604.414.408.276.611.675.611 1.197 0 .15-.019.307-.055.469-.15.69-.452 1.201-.908 1.531-.45.33-1.069.496-1.855.496h-1.217l-.414 1.973a.26.26 0 01-.1.162c-.054.042-.11.063-.17.063h-1.224a.187.187 0 01-.155-.063.173.173 0 01-.027-.162l1.244-5.856a.256.256 0 01.098-.162c.054-.042.112-.063.172-.063zm-4.287 1.207c-.337 0-.624.099-.864.297-.234.198-.402.502-.504.91-.108.402-.216.894-.324 1.477a1.93 1.93 0 00-.037.379c0 .552.289.828.865.828.336 0 .624-.099.856-.297.24-.198.411-.502.513-.91.138-.565.243-1.056.315-1.477a2.1 2.1 0 00.037-.389c0-.546-.287-.818-.857-.818zm5.449.045l-.342 1.611h1.035c.253 0 .472-.069.659-.207.192-.138.317-.336.377-.594.018-.102.027-.191.027-.27 0-.174-.05-.306-.152-.396-.102-.096-.278-.144-.524-.144z" />
    </svg>
  );
}

function PayPalIcon({ color, ...props }: IconProps & { color?: string }) {
  if (color === "currentColor") {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="-1.5 0 20 20"
        fill="currentColor"
        {...props}
      >
        <path d="M16.475 5.9C15.261 11.117 11.556 12 6.579 12L5.078 20h2.747c.5 0 .71-.341.802-.821.685-4.331.589-3.622.651-4.3.06-.378.386-.879.77-.879 3.65 0 6.07-1.055 6.81-4.842.262-1.347.176-2.386-.383-3.258M5.134 11.86L4.012 18H.535C.206 18-.046 17.598.007 17.274L2.603.751C2.67.319 3.045 0 3.484 0h6.234C13.41 0 15.895 1.401 15.32 5.054 14.314 11.56 8.737 11 6.2 11c-.44 0-.996.428-1.065.86" />
      </svg>
    );
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="7 3 37.4 45" {...props}>
      <path
        fill="#002991"
        d="M38.914 13.35c0 5.574-5.144 12.15-12.927 12.15H18.49l-.368 2.322L16.373 39H7.056l5.605-36h15.095c5.083 0 9.082 2.833 10.555 6.77a9.687 9.687 0 01.603 3.58z"
      />
      <path
        fill="#60CDFF"
        d="M44.284 23.7A12.894 12.894 0 0131.53 34.5h-5.206L24.157 48H14.89l1.483-9 1.75-11.178.367-2.322h7.497c7.773 0 12.927-6.576 12.927-12.15 3.825 1.974 6.055 5.963 5.37 10.35z"
      />
      <path
        fill="#008CFF"
        d="M38.914 13.35C37.31 12.511 35.365 12 33.248 12h-12.64L18.49 25.5h7.497c7.773 0 12.927-6.576 12.927-12.15z"
      />
    </svg>
  );
}

function KofiIcon({ color, ...props }: IconProps & { color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill={color ?? "currentColor"}
      {...props}
    >
      <path d="M23.881 8.948c-.773-4.085-4.859-4.593-4.859-4.593H.723c-.604 0-.679.798-.679.798s-.082 7.324-.022 11.822c.164 2.424 2.586 2.672 2.586 2.672s8.267-.023 11.966-.049c2.438-.426 2.683-2.566 2.658-3.734 4.352.24 7.422-2.831 6.649-6.916zm-11.062 3.511c-1.246 1.453-4.011 3.976-4.011 3.976s-.121.119-.31.023c-.076-.057-.108-.09-.108-.09-.443-.441-3.368-3.049-4.034-3.954-.709-.965-1.041-2.7-.091-3.71.951-1.01 3.005-1.086 4.363.407 0 0 1.565-1.782 3.468-.963 1.904.82 1.832 3.011.723 4.311zm6.173.478c-.928.116-1.682.028-1.682.028V7.284h1.77s1.971.551 1.971 2.638c0 1.913-.985 2.667-2.059 3.015z" />
    </svg>
  );
}

function PatreonIcon({ color, ...props }: IconProps & { color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1080 1080"
      fill={color ?? "currentColor"}
      {...props}
    >
      <path d="M1033.05 324.45c-.19-137.9-107.59-250.92-233.6-291.7-156.48-50.64-362.86-43.3-512.28 27.2C106.07 145.41 49.18 332.61 47.06 519.31c-1.74 153.5 13.58 557.79 241.62 560.67 169.44 2.15 194.67-216.18 273.07-321.33 55.78-74.81 127.6-95.94 216.01-117.82C929.71 603.22 1033.27 483.3 1033.05 324.45z" />
    </svg>
  );
}

const providerIconMap: Record<
  ProviderId,
  React.FC<IconProps & { color?: string }>
> = {
  ethereum: EthereumIcon,
  bitcoin: BitcoinIcon,
  solana: SolanaIcon,
  polygon: PolygonIcon,
  base: BaseIcon,
  arbitrum: ArbitrumIcon,
  optimism: OptimismIcon,
  avalanche: AvalancheIcon,
  bnb: BnbIcon,
  litecoin: LitecoinIcon,
  paypal: PayPalIcon,
  kofi: KofiIcon,
  patreon: PatreonIcon,
};

/**
 * Provider icon with brand color by default.
 * Pass `muted` to use `currentColor` instead (inherits text color).
 */
function ProviderIcon({
  provider,
  muted = false,
  className,
  ...props
}: { provider: ProviderId; muted?: boolean } & IconProps) {
  const Icon = providerIconMap[provider];
  const color = muted ? "currentColor" : brandColors[provider];
  return (
    <Icon
      color={color}
      className={cn("size-4 shrink-0", className)}
      aria-hidden="true"
      {...props}
    />
  );
}

export { brandColors, ProviderIcon, providerIconMap };
